import type {
    BoundaryPartRequest,
    CreateBoundaryRequest,
    FlightResponse
} from 'generatedTypes/flight/models';
import { useEffect, useState } from 'react';

import Map from 'components/Map';
import {
    DEFAULT_CENTER,
    DEFAULT_MAX_ZOOM,
    DEFAULT_MIN_ZOOM,
    DEFAULT_ZOOM,
    MAP_STYLE
} from 'components/Map/constants';
import type { Geometry } from 'components/Map/types';
import Page from 'components/Page';

import { useFloatingResourceAddContext } from 'hooks/useFloatingResourceAddContext';

const geometry = {
    isEditable: false,
    parts: [
        {
            boundary: {
                coordinates: [
                    [
                        [-98.63479095198026, 39.82969316771849],
                        [-98.63479075497521, 39.83627867330301],
                        [-98.62759028302543, 39.83522549898217],
                        [-98.63479095198026, 39.82969316771849]
                    ]
                ],
                type: 'Polygon'
            },
            boundaryType: 'APPLY',
            geometryType: 'POLYGON'
        },
        {
            boundary: {
                coordinates: [
                    [
                        [-98.54794234417595, 39.839735486648785],
                        [-98.5488232693663, 39.83970225345434],
                        [-98.5496957082283, 39.83960287402083],
                        [-98.55055125625911, 39.83943830571066],
                        [-98.55138167181522, 39.839210133868114],
                        [-98.55217895557033, 39.83892055653381],
                        [-98.55293542762901, 39.83857236325247],
                        [-98.55364380155048, 39.83816890817901],
                        [-98.55429725456712, 39.83771407774303],
                        [-98.55488949331993, 39.83721225318431],
                        [-98.55541481447652, 39.836668268321695],
                        [-98.55586815964763, 39.83608736296329],
                        [-98.55624516407379, 39.83547513240825],
                        [-98.55654219861393, 39.83483747352785],
                        [-98.55675640463394, 39.83418052794633],
                        [-98.55688572146096, 39.833510622869824],
                        [-98.55692890614252, 39.832834210134216],
                        [-98.5568855453229, 39.832157804059555],
                        [-98.55675605912671, 39.83148791870989],
                        [-98.5565416970152, 39.830831005163034],
                        [-98.55624452565971, 39.830193389394026],
                        [-98.55586740895207, 39.82958121137038],
                        [-98.5554139803483, 39.8290003659446],
                        [-98.55488860781415, 39.82845644611268],
                        [-98.55429635171323, 39.827954689183635],
                        [-98.55364291604468, 39.82749992637732],
                        [-98.55293459350078, 39.82709653633455],
                        [-98.55217820487476, 39.82674840298585],
                        [-98.55138103340113, 39.826458878182926],
                        [-98.55055075466036, 39.82623074945179],
                        [-98.54969536272107, 39.82606621317628],
                        [-98.54882309322824, 39.82596685346961],
                        [-98.54794234417595, 39.825933626936106],
                        [-98.54706159512367, 39.82596685346961],
                        [-98.54618932563083, 39.82606621317628],
                        [-98.54533393369154, 39.82623074945179],
                        [-98.54450365495077, 39.826458878182926],
                        [-98.54370648347714, 39.82674840298585],
                        [-98.54295009485112, 39.82709653633455],
                        [-98.54224177230722, 39.82749992637732],
                        [-98.54158833663867, 39.827954689183635],
                        [-98.54099608053775, 39.82845644611268],
                        [-98.54047070800358, 39.8290003659446],
                        [-98.5400172793998, 39.82958121137038],
                        [-98.5396401626922, 39.830193389394026],
                        [-98.5393429913367, 39.830831005163034],
                        [-98.53912862922517, 39.83148791870989],
                        [-98.53899914302899, 39.832157804059555],
                        [-98.53895578220938, 39.832834210134216],
                        [-98.53899896689094, 39.833510622869824],
                        [-98.53912828371796, 39.83418052794633],
                        [-98.53934248973798, 39.83483747352785],
                        [-98.5396395242781, 39.83547513240825],
                        [-98.54001652870427, 39.83608736296329],
                        [-98.54046987387538, 39.836668268321695],
                        [-98.54099519503195, 39.83721225318431],
                        [-98.54158743378478, 39.83771407774303],
                        [-98.54224088680142, 39.83816890817901],
                        [-98.54294926072288, 39.83857236325247],
                        [-98.54370573278156, 39.83892055653381],
                        [-98.54450301653667, 39.839210133868114],
                        [-98.54533343209279, 39.83943830571066],
                        [-98.5461889801236, 39.83960287402083],
                        [-98.54706141898559, 39.83970225345434],
                        [-98.54794234417595, 39.839735486648785]
                    ]
                ],
                type: 'Polygon'
            },
            boundaryType: 'NO_ENTRY',
            geometryType: 'CIRCLE'
        } as BoundaryPartRequest
    ] as BoundaryPartRequest[]
};

const flightPaths = [
    {
        flightPath: {
            coordinates: [
                [-98.6083768893806, 39.828084467955335],
                [-98.6083768893806, 39.830447988177895],
                [-98.6100939079364, 39.83072165368722],
                [-98.61002911478327, 39.83218947736009],
                [-98.6085064756863, 39.83236362384963],
                [-98.60815011334493, 39.83328410510495],
                [-98.6080205270387, 39.833707024789476],
                [-98.60581755983529, 39.83452797909581],
                [-98.60218914326433, 39.83457773358654],
                [-98.60176798776983, 39.83355775931534],
                [-98.60345260974883, 39.833707024789476],
                [-98.60604433587095, 39.83380653492509],
                [-98.60721061262551, 39.833358738179925],
                [-98.60766416469681, 39.83251289191796],
                [-98.60623871532978, 39.83238850188343],
                [-98.60435971389143, 39.83281142708228],
                [-98.60238352272368, 39.832736793412636],
                [-98.60105526308605, 39.83201533042822],
                [-98.60163840146363, 39.83139337349783],
                [-98.60397095497326, 39.831144589148835],
                [-98.60565557695276, 39.83171679180475],
                [-98.60792333730929, 39.83141825188298],
                [-98.60740499208487, 39.830572381726455],
                [-98.60552599064651, 39.83042310944052],
                [-98.60212435011174, 39.8300996850426],
                [-98.6015736083105, 39.82905476504317],
                [-98.60435971389143, 39.82912940271336],
                [-98.60643309478914, 39.829502589846385],
                [-98.60747151472586, 39.828791156970425],
                [-98.60585168589945, 39.82804477294766],
                [-98.60238525221155, 39.82804477294766],
                [-98.60183451041084, 39.82717398133792]
            ],
            type: 'LineString'
        },
        sprayPath: {
            coordinates: [
                [
                    [-98.6083768893806, 39.828084467955335],
                    [-98.6083768893806, 39.830447988177895],
                    [-98.6100939079364, 39.83072165368722],
                    [-98.61002911478327, 39.83218947736009],
                    [-98.6085064756863, 39.83236362384963],
                    [-98.60815011334493, 39.83328410510495],
                    [-98.6080205270387, 39.833707024789476]
                ],
                [
                    [-98.60581755983529, 39.83452797909581],
                    [-98.60218914326433, 39.83457773358654],
                    [-98.60176798776983, 39.83355775931534],
                    [-98.60345260974883, 39.833707024789476],
                    [-98.60604433587095, 39.83380653492509],
                    [-98.60721061262551, 39.833358738179925],
                    [-98.60766416469681, 39.83251289191796],
                    [-98.60623871532978, 39.83238850188343]
                ],
                [
                    [-98.60105526308605, 39.83201533042822],
                    [-98.60163840146363, 39.83139337349783],
                    [-98.60397095497326, 39.831144589148835],
                    [-98.60565557695276, 39.83171679180475],
                    [-98.60792333730929, 39.83141825188298],
                    [-98.60740499208487, 39.830572381726455],
                    [-98.60552599064651, 39.83042310944052],
                    [-98.60212435011174, 39.8300996850426]
                ]
            ],
            type: 'MultiLineString'
        },
        swathWidthMeters: 20
    }
];

const MapTest = ({ testId = 'mapTest' }) => {
    const { setVisible } = useFloatingResourceAddContext();
    const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);

    const [loadedGeometry, setLoadedGeometry] = useState<Geometry>();

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setLoadedGeometry(geometry);
        }, 250);

        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        setVisible(false);
    }, [setVisible]);

    const mapOptions = {
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
        center: DEFAULT_CENTER,
        mapStyle: MAP_STYLE,
        maxZoom: DEFAULT_MAX_ZOOM,
        minZoom: DEFAULT_MIN_ZOOM,
        zoom: DEFAULT_ZOOM
    };

    const interactionOptions = {
        hasDrawingControls: isDrawingEnabled,
        hasMapControls: true
    };

    const handleDrawingEnabledChange = () => {
        setIsDrawingEnabled(!isDrawingEnabled);
    };

    const onComplete = (result: CreateBoundaryRequest) => {
        console.debug(result);
    };

    const onClose = () => {
        console.debug('MapTest: OnClose');
        setIsDrawingEnabled(false);
    };

    return (
        <Page testId={testId}>
            <Map
                flightPaths={flightPaths as unknown as FlightResponse[]}
                geometry={loadedGeometry}
                mapInteractionOptions={interactionOptions}
                mapOptions={mapOptions}
                onClickEditMap={handleDrawingEnabledChange}
                onClose={onClose}
                onComplete={onComplete}
            />
        </Page>
    );
};

export default MapTest;
